import { memo, Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from 'framer-motion';
import hero_section_images from '../image/here_section/hero_section_images.webp';
import step1 from "../image/step1.webp";
import step2 from "../image/step2.webp";
import step3 from "../image/step3.webp";
import changeCV_image from "../image/changeCV_image.webp";
import '../css/modern-home.css';
import { CheckCircle, Globe, Shield, Zap } from "lucide-react";

// ---- Lazy‑load all custom components ----
const GoogleAd = lazy(() => import("../component/adFolder/GoogleAd.jsx"));
const CrouselTechnical = lazy(() => import('./CrouselTechnical'));
const FAQSection = lazy(() => import('./FAQSection'));
const ATSMatcherUI = lazy(() => import('./ATSMatcherUI'));
const ResumeUploader = lazy(() => import('../resume-parser/components/ResumeUploader.jsx'));
const WhyResumeEraIsFree = lazy(() => import("./WhyResumeEraIsFree.jsx"));
const CategoryArticles = lazy(() => import("./CategoryArticles.jsx"));

// ---- Fallback component ----
const Loader = () => <div style={{ minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>;

const preloadCriticalStyles = () => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'style';
  link.href = '/modern-home.css';
  document.head.appendChild(link);

  setTimeout(() => {
    const styleLink = document.createElement('link');
    styleLink.rel = 'stylesheet';
    styleLink.href = '/modern-home.css';
    document.head.appendChild(styleLink);
  }, 100);
};

const useIntersectionObserver = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
};

const Home = ({ images, techImages, CoverImage, shadi_Card }) => {
  useIntersectionObserver();
  const [stats, setStats] = useState({
  total_downloads: 0,
  today_downloads: 0
});

useEffect(() => {
  fetch("https://resumeera.xyz/user-tracking-system/download-count.php")
    .then(res => res.json())
    .then(data => {
      setStats(data);
    })
    .catch(err => console.error(err));
}, []);

  useEffect(() => {
    preloadCriticalStyles();

    // Performance monitoring
    if (window.performance) {
      const perfData = window.performance.getEntriesByType('navigation')[0];
      console.log('Page Load Time:', perfData.loadEventEnd - perfData.fetchStart, 'ms');
    }
  }, []);

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };
  const floatingVariants = {
    initial: { y: 0 },
    animate: {
      y: [0, -1, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };


  return (
    <>
      <div className="modern-home-container" style={{ width: '100%' }}>
        <div className="hero-conten" style={{ marginLeft: '0px', width: '100%', background: 'inherit' }}>
          <h1 className="hero-title hero-title hero-title-line highlight"
            style={{
              paddingTop: '20px',
              fontSize: window.innerWidth < 768 ? '30px' : '50px',
              fontWeight: '900',
              width: '100%',
            }}>
            Free Resume Builder - No Sign Up - No Logging - No Payment | Resumeera
            {/* Hero Section - LCP Critical */}
          </h1>
        </div>
        <section className="hero-section" role="banner">
          <div className="hero-content">
            <div className="hero-text animate-on-scroll">
              <h2 className="hero-title hero-title-line highlight">

                create Your
                Resume in Minutes
                Free & ATS-Friendly"
              </h2>

              <p className="hero-description">
                Build stunning, professional resumes with our free Resume Builder.
                Choose from modern templates, customize instantly, and download as PDF.
              </p>

              <div className="hero-stats">

                <div className="stat-item">
                  <span className="stat-number">5★</span>
                  <span className="stat-label">User Rating</span>
                </div>
                <motion.div
                  variants={itemVariants}
                  className="flex gap-8 pt-8 border-t border-blue-100"
                >
                  <div>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                      className="text-3xl font-bold text-blue-600"
                    >
                      50+
                    </motion.div>
                    <p className="text-gray-600 text-sm">Templates</p>
                  </div>
                  <div>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                      className="text-3xl font-bold text-teal-600"
                    >
                      100%
                    </motion.div>
                    <p className="text-gray-600 text-sm">Free Forever</p>
                  </div>
                  <div>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7, duration: 0.5 }}
                      className="text-3xl font-bold text-orange-500"
                    >
                      0$
                    </motion.div>
                    <p className="text-gray-600 text-sm">Hidden Costs</p>
                  </div>
                  <hr />
                  
                </motion.div>
                
              </div>
<div className="stats-box">
  <div>
    <h2>
      
    </h2>
    <p>Total <span style={{ fontWeight: 'bold' }}>{stats.total_downloads.toLocaleString()}+</span> Resume Downloads</p>
  </div>

  <div>
    <h2>
      
    </h2>
    <p>Today's <span style={{ fontWeight: 'bold' }}>{stats.today_downloads.toLocaleString()}</span> Resume Downloads</p>
  </div>
</div>
              <div className="hero-actions">
                <Link to="/choice-to-making-resume" className="btn btn-primary btn-large">
                  Build Free Resume
                  <span className="btn-icon">→</span>
                </Link>
                <Link to="/choice-to-making-resume" className="btn btn-secondary btn-large">
                  View Templates
                </Link>
              </div>

            </div>

            <motion.div
              variants={floatingVariants}
              initial="initial"
              animate="animate"
              className="hidden md:flex justify-center"
            >
              <motion.div
                whileHover={{ scale: 1.05, rotateZ: 2 }}
                transition={{ type: 'spring', stiffness: 300, damping: 10 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl blur-2xl opacity-20" />
                <img
                  src={hero_section_images}
                  style={{ height: '500px' }}
                  alt="Professional Resume Template"
                  className="relative w-full max-w-sm rounded-2x"
                />
              </motion.div>
            </motion.div>
          </div>
        </section>
    
        <section id="features" className="py-20 bg-white">
          <div className="containe">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: '-100px' }}
              className="text-center mb-16"
            >
              <h2 className=" mb-4 " style={{
                fontSize: window.innerWidth < 768 ? '30px' : '50px',
                fontWeight: '900',
                width: '100%',
              }}
              >
                Why Choose Resumeera?
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Everything you need to create a professional resume, completely free
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {/* Feature 1 */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 p-4
              rounded-xl border border-blue-200 hover:shadow-lg transition-shadow"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4"
                >
                  <CheckCircle className="w-6 h-6 text-white" />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">All Template Free</h3>
                <p className="text-gray-600">
                  50+ expertly designed templates created by HR professionals
                </p>
              </motion.div>

              {/* Feature 2 */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-xl border border-teal-200 hover:shadow-lg transition-shadow"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4"
                >
                  <Zap className="w-6 h-6 text-white" />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Lightning Fast</h3>
                <p className="text-gray-600">
                  Create and download your resume in seconds, no delays
                </p>
              </motion.div>

              {/* Feature 3 */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200 hover:shadow-lg transition-shadow"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4"
                >
                  <Shield className="w-6 h-6 text-white" />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">ATS-Friendly</h3>
                <p className="text-gray-600">
                  All templates are optimized to pass Applicant Tracking Systems
                </p>
              </motion.div>

              {/* Feature 4 */}
              <motion.div
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-blue-50 to-teal-100 p-4 rounded-xl border border-blue-200 hover:shadow-lg transition-shadow"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4"
                >
                  <Globe className="w-6 h-6 text-white" />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Accessible Anywhere</h3>
                <p className="text-gray-600">
                  Use on any device, anytime, anywhere with no registration required
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>
        <section className="testimonials-section d-md-flex">
          <h2 style={{
            fontSize: window.innerWidth < 768 ? '30px' : '50px',
            fontWeight: '900',
            width: '100%',
          }}
          >What Our Users Say</h2>
          <div className="keywords-section">
            <span>
              <a href="https://resumeera.xyz/free-resume-builder-for-it-professional"
                rel="noopener noreferrer"
              >
                free resume builder for it professional
              </a>
            </span>

            <span>
              <a href="https://resumeera.xyz/free-resume-builder-india"
                rel="noopener noreferrer"
              >
                free resume builder for india
              </a>
            </span>

            <span>
              <a href="https://resumeera.xyz/instant-resume-download-no-payment"
                rel="noopener noreferrer"
              >
                instant resume download no payment
              </a>
            </span>

            <span>
              <a href="https://resumeera.xyz/online-cv-maker-no-login"
                rel="noopener noreferrer"
              >
                online cv maker no login
              </a>
            </span>

            <span>
              <a href="https://resumeera.xyz/best-resume-template-for-india"
                rel="noopener noreferrer"
              >
                best resume template for India
              </a>
            </span>
          </div>
        </section>

        <section className='testimonials-section'>
          <div className="section-header">
            <h2 className="section-title" style={{
              fontSize: window.innerWidth < 768 ? '30px' : '50px',
              fontWeight: '900',
              width: '100%',
            }}
            >ATS-Friendly Resume Templates</h2>
            <p className="section-subtitle">Our resume is powered by cutting-edge technologies for the best experience</p>
          </div>
          <Suspense fallback={<Loader />}>
          <CrouselTechnical techImages={techImages} />
          </Suspense>
        </section>

        {/* How It Works */}
        <section className="how-it-works-section animate-on-scroll">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title" style={{
                fontSize: window.innerWidth < 768 ? '30px' : '50px',
                fontWeight: '900',
                width: '100%',
              }}
              >How It Works</h2>
              <p className="section-subtitle">Three simple steps to your professional resume</p>
            </div>

            <div className="steps-container">
              <div className="step-item animate-on-scroll">
                <div className="step-number">1</div>
                <div className="step-content">
                  <div className="step-image">
                    <img src={step1} alt="Choose Template" loading="lazy" />
                  </div>
                  <h3 className="step-title">Choose Template</h3>
                  <p className="step-description">Select from our collection of professional, ATS-friendly templates designed by experts.</p>
                </div>
              </div>

              <div className="step-item animate-on-scroll">
                <div className="step-number">2</div>
                <div className="step-content">
                  <div className="step-image">
                    <img src={step2} alt="Fill Details" loading="lazy" />
                  </div>
                  <h3 className="step-title">Fill Your Details</h3>
                  <p className="step-description">Add your information using our intuitive form with AI-powered suggestions.</p>
                </div>
              </div>

              <div className="step-item animate-on-scroll">
                <div className="step-number">3</div>
                <div className="step-content">
                  <div className="step-image">
                    <img src={step3} alt="Download PDF" loading="lazy" />
                  </div>
                  <h3 className="step-title">Free Download in PDF</h3>
                  <p className="step-description">Preview and download your professional resume as a high-quality PDF instantly.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="benefits-section animate-on-scroll">
          <div className="container">
            <div className="benefits-content">
              <div className="benefits-text">
                <h2 className="benefits-title" style={{
                  fontSize: window.innerWidth < 768 ? '30px' : '50px',
                  fontWeight: '900',
                  width: '100%',
                }}
                >Change Your Resume, Change Your Life!</h2>
                <ul className="benefits-list">
                  <li>Professional templates designed by career experts</li>
                  <li>ATS-optimized to pass automated screenings</li>
                  <li>Instant PDF download with no watermarks</li>
                  <li>Mobile-responsive design for any device</li>
                  <li>Privacy-focused - no account required</li>
                </ul>
                <Link to="/choice-to-making-resume" className="btn btn-primary btn-large">
                  Create Your Resume Now
                </Link>
              </div>
              <div className="benefits-image">
                <img src={changeCV_image} alt="Change Your CV" loading="lazy" style={{
                  width: window.innerWidth < 768 ? '100%' : '100%',
                  height: '100%'

                }} />
              </div>
            </div>
          </div>
        </section>
        <section className="why-resumeera-section animate-on-scroll">
          <Suspense fallback={<Loader />}>
            <WhyResumeEraIsFree />
          </Suspense>
        </section>
        {/* <section>
                <Suspense fallback={<Loader />}>
                  <CategoryArticles />
                </Suspense>

        </section>        FAQ Section */}
        <section id="faq" className="faq-sections animate-on-scroll">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Frequently Asked Questions</h2>
              <p className="section-subtitle">
                Everything you need to know about our free resume builder
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold mb-4">
                  Free?
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Is ResumeEra really a free resume builder?
                </h3>
                <p className="text-gray-600 leading-7">
                  Yes. ResumeEra is a free resume builder that helps users create professional resumes online without hidden charges or forced payment before getting started.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-sm font-semibold mb-4">
                  ATS?
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Are ResumeEra templates ATS-friendly?
                </h3>
                <p className="text-gray-600 leading-7">
                  Yes. Our resume templates are ATS-friendly and use clear structure, readable formatting, and standard sections that help recruiters and applicant tracking systems scan resumes more easily.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-orange-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-sm font-semibold mb-4">
                  Download?
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Can I download my resume as PDF?
                </h3>
                <p className="text-gray-600 leading-7">
                  Yes. You can create your resume online and download it instantly as a PDF for job applications, recruiter sharing, and direct submissions.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold mb-4">
                  Sign Up?
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Do I need to sign up to use ResumeEra?
                </h3>
                <p className="text-gray-600 leading-7">
                  ResumeEra is completely free to use. Create, edit, and download your resume without sign-up, login, or payment. Our goal is to make professional resume building fast, simple, and accessible to everyone.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-sm font-semibold mb-4">
                  Freshers?
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Is this free resume builder good for freshers?
                </h3>
                <p className="text-gray-600 leading-7">
                  Absolutely. ResumeEra is useful for freshers, students, and first-time job seekers who want a professional resume template with simple editing and ATS-friendly formatting.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-4 border border-orange-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-sm font-semibold mb-4">
                  Who?
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Who can use this resume builder?
                </h3>
                <p className="text-gray-600 leading-7">
                  Students, freshers, experienced professionals, job switchers, and candidates returning to work can all use ResumeEra to create a professional resume quickly.
                </p>
              </div>

            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default memo(Home);
